import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Check,
  Play,
  Scissors,
  User,
  Building2,
  Calendar,
  DollarSign,
  Gift,
  Star,
  Users,
  Clock,
  Camera,
  Share2,
  CreditCard,
  BarChart3,
  Settings,
  Bell,
  Search,
  MapPin,
  Heart,
  MessageSquare,
  Shield,
  Sparkles,
  Award,
  Wallet,
  FileText
} from "lucide-react";

// Role-specific onboarding steps
const ONBOARDING_STEPS = {
  client: {
    title: "Client Guide",
    subtitle: "Find stylists, book appointments & earn rewards",
    color: "from-blue-500 to-cyan-500",
    icon: User,
    steps: [
      {
        id: "welcome",
        title: "Welcome to MyHairChair!",
        description: "Your personal beauty booking app. Find amazing stylists, book appointments 24/7, and earn rewards with every visit.",
        icon: Sparkles,
        tips: [
          "Browse stylists by location, specialty, or rating",
          "Book appointments anytime, anywhere",
          "Earn loyalty points on every visit"
        ],
        action: { label: "Let's Go!", path: "/dashboard" }
      },
      {
        id: "find-stylist",
        title: "Find Your Perfect Stylist",
        description: "Browse through our network of talented barbers and stylists. Filter by location, specialty, price, and ratings.",
        icon: Search,
        tips: [
          "Use filters to narrow down your search",
          "Check stylist portfolios and reviews",
          "Save your favorites for quick access"
        ],
        action: { label: "Browse Stylists", path: "/search" }
      },
      {
        id: "book-appointment",
        title: "Book Your Appointment",
        description: "Select a service, pick your preferred date and time, and confirm your booking in just a few taps.",
        icon: Calendar,
        tips: [
          "View real-time availability",
          "Choose from various services",
          "Add notes for your stylist",
          "📅 Download to your calendar after booking!"
        ],
        action: { label: "Book Now", path: "/book" }
      },
      {
        id: "earn-rewards",
        title: "Earn & Redeem Rewards",
        description: "Every booking earns you loyalty points! Redeem them for discounts, free services, and exclusive perks.",
        icon: Gift,
        tips: [
          "100 welcome bonus points on signup",
          "1 point per dollar spent",
          "Refer friends for bonus points"
        ],
        action: { label: "View Rewards", path: "/dashboard" }
      },
      {
        id: "leave-review",
        title: "Share Your Experience",
        description: "After your appointment, leave a review to help others find great stylists and earn extra loyalty points!",
        icon: Star,
        tips: [
          "Earn 10 points for each review",
          "Help stylists grow their business",
          "Your feedback improves the community"
        ],
        action: { label: "My Reviews", path: "/dashboard" }
      }
    ]
  },
  stylist: {
    title: "Stylist/Barber Guide",
    subtitle: "Manage bookings, grow your business & get paid",
    color: "from-amber-500 to-orange-500",
    icon: Scissors,
    steps: [
      {
        id: "welcome",
        title: "Welcome, Beauty Professional!",
        description: "Take control of your career with MyHairChair. Manage your schedule, accept payments, and grow your client base.",
        icon: Sparkles,
        tips: [
          "Set up your profile to attract clients",
          "Manage bookings from one dashboard",
          "Accept multiple payment methods"
        ],
        action: { label: "Go to Dashboard", path: "/stylist" }
      },
      {
        id: "complete-profile",
        title: "Complete Your Profile",
        description: "Add your professional details, bio, and profile photo. A complete profile gets 3x more bookings!",
        icon: User,
        tips: [
          "Add a professional profile photo",
          "Write a compelling bio",
          "Select your professional type"
        ],
        action: { label: "Edit Profile", path: "/stylist" }
      },
      {
        id: "add-services",
        title: "Add Your Services",
        description: "List the services you offer with pricing and duration. Clients can then book directly.",
        icon: DollarSign,
        tips: [
          "Use templates or create custom services",
          "Set competitive pricing",
          "Include service descriptions"
        ],
        action: { label: "Add Services", path: "/stylist" }
      },
      {
        id: "set-schedule",
        title: "Set Your Availability",
        description: "Define your working hours and days. Block off personal time whenever you need it.",
        icon: Clock,
        tips: [
          "Set your weekly schedule",
          "Block off vacation days",
          "Sync with Google Calendar"
        ],
        action: { label: "Set Schedule", path: "/stylist" }
      },
      {
        id: "upload-gallery",
        title: "Showcase Your Work",
        description: "Upload photos of your best work. Your portfolio is your best marketing tool!",
        icon: Camera,
        tips: [
          "Add high-quality photos",
          "Show variety in your work",
          "Update regularly with new styles"
        ],
        action: { label: "Upload Photos", path: "/stylist" }
      },
      {
        id: "setup-payments",
        title: "💳 Set Up Stripe Connect",
        description: "Connect your bank account to accept card payments. Without this, clients can only pay cash, Zelle, or CashApp.",
        icon: CreditCard,
        tips: [
          "Go to 'Fees & Earnings' tab in your dashboard",
          "Click 'Set Up Stripe Connect'",
          "Enter business info on Stripe's secure page",
          "Add your bank account for direct deposits",
          "Takes 5-10 minutes to complete"
        ],
        action: { label: "Set Up Payouts", path: "/stylist" },
        isRequired: true
      },
      {
        id: "pro-minisite",
        title: "🌟 Get Your Pro Mini-Site",
        description: "Upgrade to Pro and get your own professional website with custom URL, QR code, and booking page. Perfect for business cards!",
        icon: Sparkles,
        tips: [
          "Custom URL: myhairchair.com/p/yourname",
          "Shareable QR code for print materials",
          "Professional booking page with your branding",
          "Business hours & booking policies display",
          "Portfolio gallery (up to 5 images)"
        ],
        action: { label: "Setup Mini-Site", path: "/stylist" },
        isPremium: true
      },
      {
        id: "share-booking",
        title: "Share Your Booking Link",
        description: "Share your unique booking link on social media and with clients to start getting bookings.",
        icon: Share2,
        tips: [
          "Post on Instagram & Facebook",
          "Add to your bio/linktree",
          "Text to existing clients"
        ],
        action: { label: "Get Booking Link", path: "/stylist" }
      },
      {
        id: "manage-bookings",
        title: "Manage Your Bookings",
        description: "View upcoming appointments, confirm requests, and manage your daily schedule all in one place.",
        icon: Calendar,
        tips: [
          "Confirm or decline requests",
          "Send reminders to clients",
          "Track your earnings"
        ],
        action: { label: "View Bookings", path: "/stylist" }
      },
      {
        id: "respond-reviews",
        title: "Build Your Reputation",
        description: "Respond to client reviews to show you care. Great reviews = more bookings!",
        icon: MessageSquare,
        tips: [
          "Thank clients for positive reviews",
          "Address concerns professionally",
          "Ask satisfied clients for reviews"
        ],
        action: { label: "View Reviews", path: "/stylist" }
      }
    ]
  },
  owner: {
    title: "Salon Owner Guide",
    subtitle: "Manage your salon, stylists & booth renters",
    color: "from-purple-500 to-pink-500",
    icon: Building2,
    steps: [
      {
        id: "welcome",
        title: "Welcome, Salon Owner!",
        description: "MyHairChair gives you powerful tools to manage your entire salon operation from one dashboard.",
        icon: Sparkles,
        tips: [
          "Manage booth renters easily",
          "Track revenue and analytics",
          "Automate rent collection"
        ],
        action: { label: "Go to Dashboard", path: "/stylist" }
      },
      {
        id: "setup-salon",
        title: "Set Up Your Salon",
        description: "Add your salon details, location, and hours of operation.",
        icon: Building2,
        tips: [
          "Add your business name",
          "Set your location for clients to find you",
          "Upload photos of your salon"
        ],
        action: { label: "Salon Settings", path: "/stylist" }
      },
      {
        id: "add-booth-renters",
        title: "Manage Booth Renters",
        description: "Add stylists who rent chairs/booths from you. Set their rent amount and payment schedule.",
        icon: Users,
        tips: [
          "Add renter email and rent amount",
          "Set weekly or monthly payment",
          "Track payment history"
        ],
        action: { label: "Booth Management", path: "/stylist" }
      },
      {
        id: "collect-rent",
        title: "Automate Rent Collection",
        description: "Set up automatic rent reminders and accept online rent payments through the platform.",
        icon: Wallet,
        tips: [
          "Automatic email/SMS reminders",
          "Accept credit card payments",
          "Send overdue notices"
        ],
        action: { label: "Payment Settings", path: "/stylist" }
      },
      {
        id: "view-analytics",
        title: "Track Revenue & Analytics",
        description: "Monitor your salon's performance with detailed analytics on bookings, revenue, and occupancy.",
        icon: BarChart3,
        tips: [
          "View daily/weekly/monthly revenue",
          "Track booth occupancy rates",
          "Export reports for taxes"
        ],
        action: { label: "View Analytics", path: "/stylist" }
      },
      {
        id: "marketing",
        title: "Marketing & CRM Tools",
        description: "Send promotional emails and SMS to your clients. Keep them coming back!",
        icon: Bell,
        tips: [
          "Send email campaigns",
          "Text appointment reminders",
          "Create special promotions"
        ],
        action: { label: "Marketing Dashboard", path: "/stylist" }
      },
      {
        id: "accounting",
        title: "Accounting & Finances",
        description: "Track all your income and expenses. Export data for your accountant or tax filing.",
        icon: FileText,
        tips: [
          "Log business expenses",
          "Track income by category",
          "Generate financial reports"
        ],
        action: { label: "Accounting", path: "/stylist" }
      }
    ]
  }
};

const GetStartedGuide = ({ userRole = "client", onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  
  const guide = ONBOARDING_STEPS[userRole] || ONBOARDING_STEPS.client;
  const totalSteps = guide.steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const currentStepData = guide.steps[currentStep];

  const handleNext = () => {
    // Mark current step as completed
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }
    
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Guide complete
      localStorage.setItem(`guide_completed_${userRole}`, 'true');
      onComplete?.();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem(`guide_skipped_${userRole}`, 'true');
    onSkip?.();
  };

  const IconComponent = currentStepData.icon;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-stone-900 border-stone-700 text-white overflow-hidden">
        {/* Header with progress */}
        <div className={`bg-gradient-to-r ${guide.color} p-4`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <guide.icon className="w-6 h-6 text-white" />
              <span className="font-semibold text-white">{guide.title}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="text-white/70 hover:text-white hover:bg-white/20"
            >
              Skip Guide
              <X className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Progress value={progress} className="h-2 bg-white/30" />
            <span className="text-sm text-white/90 whitespace-nowrap">
              {currentStep + 1} / {totalSteps}
            </span>
          </div>
        </div>

        {/* Content */}
        <CardContent className="p-6">
          {/* Step indicator dots */}
          <div className="flex justify-center gap-2 mb-6">
            {guide.steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep 
                    ? "w-6 bg-amber-500" 
                    : completedSteps.includes(index)
                      ? "bg-green-500"
                      : "bg-stone-700"
                }`}
              />
            ))}
          </div>

          {/* Step content */}
          <div className="text-center mb-6">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${guide.color} flex items-center justify-center mx-auto mb-4`}>
              <IconComponent className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{currentStepData.title}</h2>
            <p className="text-gray-400">{currentStepData.description}</p>
          </div>

          {/* Tips */}
          <div className="bg-stone-800 rounded-xl p-4 mb-6">
            <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Tips
            </h3>
            <ul className="space-y-2">
              {currentStepData.tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="text-gray-400 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>

            <Button
              onClick={handleNext}
              className={`bg-gradient-to-r ${guide.color} hover:opacity-90 text-white px-6`}
            >
              {currentStep === totalSteps - 1 ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Complete Guide
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// How It Works Video Section (for landing page - inline, not popup)
export const HowItWorksSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Placeholder video URL - replace with actual video
  const videoUrl = "https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1";
  
  const steps = [
    {
      number: "01",
      title: "Create Your Account",
      description: "Sign up as a client, stylist, or salon owner in under 2 minutes",
      icon: User
    },
    {
      number: "02", 
      title: "Find or List Services",
      description: "Clients browse stylists. Stylists add their services and availability",
      icon: Search
    },
    {
      number: "03",
      title: "Book & Pay",
      description: "Book appointments instantly and pay securely through the app",
      icon: Calendar
    },
    {
      number: "04",
      title: "📅 Add to Calendar",
      description: "Download appointments to Google Calendar, Outlook, or Apple Calendar",
      icon: Calendar,
      isPremium: true
    },
    {
      number: "05",
      title: "Earn Rewards",
      description: "Clients earn loyalty points. Stylists grow their business",
      icon: Gift
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-gradient-to-b from-stone-900 to-black">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">
            Simple & Easy
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            How <span className="text-amber-400">MyHairChair</span> Works
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Get started in minutes. Whether you&apos;re a client looking for a stylist or a beauty professional growing your business.
          </p>
        </div>

        {/* Video Player Area */}
        <div className="mb-16">
          <div className="relative max-w-4xl mx-auto rounded-2xl overflow-hidden bg-stone-800 aspect-video">
            {!isPlaying ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-stone-800 to-stone-900">
                {/* Thumbnail/Preview */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-purple-500/10"></div>
                <div className="relative z-10 text-center">
                  <button
                    onClick={() => setIsPlaying(true)}
                    className="w-20 h-20 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 flex items-center justify-center mb-4 hover:scale-110 transition-transform shadow-2xl shadow-amber-500/30"
                    data-testid="play-video-btn"
                  >
                    <Play className="w-8 h-8 text-black ml-1" />
                  </button>
                  <p className="text-white font-semibold text-lg">Watch How It Works</p>
                  <p className="text-gray-400 text-sm">2 minute overview</p>
                </div>
              </div>
            ) : (
              <iframe
                src={videoUrl}
                title="How MyHairChair Works"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <Card key={index} className="bg-stone-800/50 border-stone-700 hover:border-amber-500/50 transition-colors group">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="text-4xl font-bold text-amber-500/30 group-hover:text-amber-500/50 transition-colors">
                    {step.number}
                  </div>
                  <div>
                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center mb-3">
                      <step.icon className="w-5 h-5 text-amber-400" />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-white font-semibold">{step.title}</h3>
                      {step.isPremium && (
                        <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-black text-xs px-2 py-0.5">
                          PRO
                        </Badge>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm">{step.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Helper function to check if guide should show
export const shouldShowGuide = (userRole) => {
  // Check if globally skipped (e.g., from magic link or returning Google user)
  const globalSkipped = localStorage.getItem("myhairchair_guide_skipped");
  if (globalSkipped === "true") {
    return false;
  }
  
  const completed = localStorage.getItem(`guide_completed_${userRole}`);
  const skipped = localStorage.getItem(`guide_skipped_${userRole}`);
  return !completed && !skipped;
};

// Helper to reset guide (for testing or re-showing)
export const resetGuide = (userRole) => {
  localStorage.removeItem(`guide_completed_${userRole}`);
  localStorage.removeItem(`guide_skipped_${userRole}`);
  localStorage.removeItem("myhairchair_guide_skipped");
};

export default GetStartedGuide;
